/**
 * Always-visible scrollbar for the mobile filter drawer's panes — in practice,
 * the hundred-name Designer list.
 *
 * Native scrollbars cannot do this: on iOS and macOS they are overlays that
 * only appear while you are scrolling, and iOS Safari ignores
 * ::-webkit-scrollbar styling altogether. So a list that ran past the screen
 * gave no sign there was more. This draws its own thin track and thumb beside
 * each pane body that overflows, keeps them in step with scrolling, resizing
 * and the designer search narrowing the list, and lets the thumb be dragged
 * or the track tapped.
 *
 * Panes are re-rendered whenever a filter changes, so bodies are picked up by
 * a MutationObserver rather than bound once.
 */
(function () {
  const BODY = '.facets-drawer-drilldown__pane-body';
  const MIN_THUMB = 32;

  /** @param {HTMLElement} body */
  function attach(body) {
    if (body.dataset.wfScrollbar === 'true') return;
    const pane = body.parentElement;
    if (!(pane instanceof HTMLElement)) return;
    body.dataset.wfScrollbar = 'true';

    const track = document.createElement('div');
    track.className = 'wf-facet-scrollbar';
    track.setAttribute('aria-hidden', 'true');
    const thumb = document.createElement('div');
    thumb.className = 'wf-facet-scrollbar__thumb';
    track.appendChild(thumb);
    pane.appendChild(track);

    let frame = 0;

    function update() {
      frame = 0;
      const viewport = body.clientHeight;
      const content = body.scrollHeight;
      const overflow = content - viewport;

      track.hidden = overflow <= 1 || viewport === 0;
      if (track.hidden) return;

      track.style.top = `${body.offsetTop}px`;
      track.style.height = `${viewport}px`;

      const thumbHeight = Math.max(MIN_THUMB, (viewport * viewport) / content);
      const travel = viewport - thumbHeight;
      thumb.style.height = `${thumbHeight}px`;
      thumb.style.transform = `translateY(${(body.scrollTop / overflow) * travel}px)`;
    }

    function schedule() {
      if (!frame) frame = requestAnimationFrame(update);
    }

    body.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    if ('ResizeObserver' in window) {
      const resize = new ResizeObserver(schedule);
      resize.observe(body);
      if (body.firstElementChild) resize.observe(body.firstElementChild);
    }
    // The designer search hides rows, which changes the height without
    // resizing the body itself.
    new MutationObserver(schedule).observe(body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden'],
      childList: true,
    });

    // Drag the thumb, or tap the track to jump there.
    thumb.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      event.stopPropagation();
      thumb.setPointerCapture(event.pointerId);
      const startY = event.clientY;
      const startScroll = body.scrollTop;
      const ratio =
        (body.scrollHeight - body.clientHeight) /
        Math.max(1, body.clientHeight - thumb.offsetHeight);

      const move = (/** @type {PointerEvent} */ e) => {
        body.scrollTop = startScroll + (e.clientY - startY) * ratio;
      };
      const up = () => {
        thumb.removeEventListener('pointermove', move);
        thumb.removeEventListener('pointerup', up);
        thumb.removeEventListener('pointercancel', up);
      };
      thumb.addEventListener('pointermove', move);
      thumb.addEventListener('pointerup', up);
      thumb.addEventListener('pointercancel', up);
    });

    track.addEventListener('pointerdown', (event) => {
      if (event.target !== track) return;
      const rect = track.getBoundingClientRect();
      const share = (event.clientY - rect.top) / rect.height;
      body.scrollTop = share * (body.scrollHeight - body.clientHeight);
    });

    update();
    // Panes slide in; measure again once they are laid out and visible.
    setTimeout(schedule, 300);
  }

  function scan(/** @type {ParentNode} */ root) {
    root.querySelectorAll(BODY).forEach((body) => {
      if (body instanceof HTMLElement) attach(body);
    });
  }

  function start() {
    scan(document);
    new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches(BODY)) attach(node);
          else scan(node);
        });
      }
    }).observe(document.body, { childList: true, subtree: true });

    // A pane becoming active is a class change, not an added node: re-measure.
    document.addEventListener('click', () => {
      requestAnimationFrame(() =>
        document.querySelectorAll(BODY).forEach((body) => body.dispatchEvent(new Event('scroll'))),
      );
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
